import _ from 'lodash';
import React, { useMemo, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, Route, Routes, useParams, useLocation } from 'react-router-dom';
import { saveReturnUrl, isLoggingOut } from '../util/returnUrl';
import MainLayout from '../layout/mainLayout/MainLayout';
import AccountDetails from '../pages/AccountDetails/AccoutDetails';
import DOA from '../pages/DOA';
import ReportingDashboard from '../pages/ReportingDashboard';
import Sponsor from '../pages/Sponsors/Sponsor';
import SponsorShow from '../pages/Sponsors/SponsorShow';
import SiteDetails from '../pages/Sponsors/SiteDetails';
import Booking from '../pages/booking';
import BookedVisitSlotDetails from '../pages/booking/BookedVisitSlotDetails';
import ConfirmVisit from '../pages/booking/ConfirmVisit';
import SlotBooking from '../pages/booking/SlotBooking';
import CreateTrial from '../pages/createTrial/CreateTrial';
import ManageConfiguration from '../pages/dashboard/ManageConfiguration';
import SiteAdminDashboard from '../pages/dashboard/SiteAdminDashboard';
import SponsorDashboard from '../pages/dashboard/SponsorDashboard';
import SubjectDashboard from '../pages/dashboard/SubjectDashboard';
import SystemAdminDashboard from '../pages/dashboard/SystemAdminDashboard';
import SubjectEnrollFormDetails from '../pages/eConsent/SubjectEnrollFormDetails';
import SubjectProfile from '../pages/eConsent/SubjectProfile';
import EnrollSubject from '../pages/enrollSubject/EnrollSubject';
import FinanceModule from '../pages/financeModule/FinanceModule';
import SponsorDetails from '../pages/financeModule/SponsorDetails';
import StudySetup from '../pages/financeModule/studySetup/StudySetup';
import LoginRouteGuard from './LoginRouteGuard';
import ForgotPassword from '../pages/login/forgotPassword';
import SiteInitiationForm from '../pages/siteIinitiation/SiteInitiationForm';
import SubjectCRFForm from '../pages/subjectCRF/SubjectCRFForm';
import SubjectDetails from '../pages/subjectDetails/SubjectDetails';
import SubjectVisitDetailCRFInfo from '../pages/subjectDetails/visitDetailCRF/SubjectVisitDetailCRFInfo';
import VisitDetailCRFInfo from '../pages/subjectDetails/visitDetailCRF/VisitDetailCRFInfo';
import SubjectWithdrawForm from '../pages/subjectWithdraw/SubjectWithdrawForm';
import TrialDetails from '../pages/trialDetails/TrialDetails';
import TrialSiteDetails from '../pages/trialSiteDetails/TrialSiteDetails';
import { getUserRole } from '../pages/util';
import VisitSchedule from '../pages/visitScheudle';
import ScheduleCalendar from '../pages/visitScheudle/ScheduleCalendar';
import SubjectSchedule from '../pages/visitScheudle/SubjectSchedule';
import {
  selectCurrentUser,
  selectIsGetMeInitialized,
  selectIsGetMeLoading,
  selectIsSystemAdmin,
  selectUserRoles,
} from '../store/slices/authSlice';
import NoTrialAccess from '../pages/login/NoTrialAccess';
import { Box, CircularProgress } from '@mui/material';
import CreateTrials from '../pages/createTrialSystemAdmin/CreateTrails';
import PageNotFound from '../pages/PageNotFound';
import PfasTrack from '../pages/pfasTrack/PfasTrack';
import PfasOrgDetails from '../pages/pfasTrack/PfasOrgDetails';
import AssignmentSetup from '../pages/pfasTrack/assignmentSetup/AssignmentSetup';
import AssignmentDetails from '../pages/pfasTrack/assignmentDetails';
import VendorDetails from '../pages/pfasTrack/assignmentDetails/VendorDetails';
import ProductCrf from '../pages/pfasTrack/assignmentDetails/ProductCrf';
import ETMFs from '../pages/eTMFsDocument';
import ETMFsDocument from '../pages/eTMFsDocument/ETMFsDocument';
import CreateFinanceTrial from '../pages/createFinanceTrial/CreateFinanceTrial';
import FinanceTrialDetails from '../pages/trialDetails/FinanceTrialDetails';
import FinanceTrialSitedetails from '../pages/trialSiteDetails/financeTrialSiteDetails/FinanceTrialSitedetails';
import FinAdminDashboard from '../pages/dashboard/FinAdminDashboard';
import FinanceReportingDashboard from '../pages/financeReportingDashboard/FinanceReportingDashboard';
import AuthProvider from '../components/AuthProvider/AuthProvider';
import { FaqList, FaqForm, FaqPreview, CategoryManagement, HelpSupport } from '../pages/faq';
import { SupportTicketList, SupportTicketDetail } from '../pages/supportTickets';

const ProtectedRoute = ({ user, roles, redirectPath = '/login' }) => {
  const location = useLocation();
  const { trialId, siteId, trialSiteId } = useParams();
  const userData = useSelector((state) => state.auth.userData);
  const allUserRoles = useSelector(selectUserRoles);
  const isGetMeInitialized = useSelector(selectIsGetMeInitialized);
  const isGetMeLoading = useSelector(selectIsGetMeLoading);
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  const sessionExpiredDialog = useSelector((state) => state.application.sessionExpiredDialog);

  const isRouteAccess = useMemo(() => {
    if (!user) return false;

    if (isSystemAdmin && roles.includes('system_admin')) {
      return true;
    }

    if (!isGetMeInitialized) return false;

    if (allUserRoles.length === 0) return false;

    const userRoles = allUserRoles.filter((roleObj) => roles.includes(roleObj.role));

    if (userRoles.some((r) => ['sponsor', 'fi_admin'].includes(r.role))) {
      return true;
    }

    if (trialId || siteId || trialSiteId) {
      return userRoles.some((roleObj) => {
        const trialMatch = !trialId || roleObj.trialId == trialId;
        const siteMatch = !siteId || roleObj.siteId == siteId;
        return trialMatch && siteMatch;
      });
    }

    return userRoles.length > 0;
  }, [trialId, siteId, trialSiteId, user, isGetMeInitialized, allUserRoles, roles, isSystemAdmin]);

  // Session expiry in progress: never show Wrong Navigation / no-trial-access.
  // MainLayout shows ExpiredDialog while user is still present; once cleared, redirect below.
  if (sessionExpiredDialog) {
    if (!user) {
      const loggingOut = isLoggingOut();
      if (!loggingOut) {
        const fullPath = location.pathname + (location.search || '');
        saveReturnUrl(fullPath);
      }
      return <Navigate to={redirectPath} replace state={loggingOut ? undefined : { from: location }} />;
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    const loggingOut = isLoggingOut();
    if (!loggingOut) {
      const fullPath = location.pathname + (location.search || '');
      saveReturnUrl(fullPath);
    }
    return <Navigate to={redirectPath} replace state={loggingOut ? undefined : { from: location }} />;
  }

  if (!isSystemAdmin && (!isGetMeInitialized || isGetMeLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSystemAdmin && allUserRoles.length === 0) {
    return <Navigate to="/no-trial-access" replace />;
  }

  if (!isRouteAccess) {
    return <PageNotFound height={'100%'} title={`Wrong Navigation`} />;
  }

  return <Outlet />;
};

const AppRoutes = () => {
  const user = useSelector(selectCurrentUser);
  const userData = useSelector((state) => state.auth.userData);
  const isSystemAdmin = useSelector(selectIsSystemAdmin);

  const userRole = getUserRole(user, null, null, userData);
  
  return (
    <AuthProvider>
      <Routes>
        <Route exact path="/login" element={<LoginRouteGuard />} />
        <Route exact path="/forgotPassword" element={<ForgotPassword />} />

        <Route path="/" element={<MainLayout />}>
          <Route
            path="/no-trial-access"
            element={user ? <NoTrialAccess /> : <Navigate to="/login" replace />}
          />
          <Route path="/help" element={user ? <HelpSupport /> : <Navigate to="/login" replace />} />
          <Route
            element={
              <ProtectedRoute
                user={user}
                roles={[
                  'site_admin',
                  'subject',
                  'site_monitor',
                  'sponsor',
                  'PI',
                  'site_coordinator',
                  'system_admin',
                  'fi_admin',
                  'data_manager',
                ]}
              />
            }
          >
            <Route
              path="/"
              element={
                userRole == 'subject' ? (
                  <SubjectDashboard />
                ) : userRole == 'site_admin' ||
                  userRole == 'PI' ||
                  userRole == 'site_coordinator' ||
                  userRole == 'site_monitor' ||
                  userRole == 'data_manager' ? (
                  <SiteAdminDashboard />
                ) : userRole == 'sponsor' ? (
                  <SponsorDashboard />
                ) : isSystemAdmin || userRole == 'system_admin' ? (
                  <Sponsor />
                ) : userRole == 'fi_admin' ? (
                  <FinAdminDashboard />
                ) : (
                  <NoTrialAccess />
                )
              }
            />
            <Route path="/accountDetails" element={<AccountDetails />} />
            {/* Public Help & Support - Available to all authenticated users */}
            <Route path="/help" element={<HelpSupport />} />
          </Route>

          <Route
            element={
              <ProtectedRoute
                user={user}
                roles={['site_admin', 'site_monitor', 'sponsor', 'PI', 'site_coordinator', 'fi_admin', 'data_manager']}
              />
            }
          >
            <Route path="/trial/:trialId?" element={<TrialDetails />} />
            <Route path="/financeTrial/:trialId?" element={<FinanceTrialDetails />} />
            <Route
              exact
              path="/financeTrial/:trialId/trial-site/:trialSiteId/site/:siteId"
              element={<FinanceTrialSitedetails />}
            />
            <Route exact path="/trial/:trialId/trial-site/:trialSiteId/site/:siteId" element={<TrialSiteDetails />} />
            <Route path="/reportingDashboard" element={<ReportingDashboard />} />
            <Route path="/financeReportingDashboard" element={<FinanceReportingDashboard />} />
            <Route
              exact
              path="/trial/:trialId/trial-site/:trialSiteId/site/:siteId/:siteInitStep"
              element={<SiteInitiationForm />}
            />
          </Route>

          <Route
            element={<ProtectedRoute user={user} roles={['site_admin', 'site_monitor', 'data_manager', 'PI', 'site_coordinator']} />}
          >
            <Route path="/schedule" element={<VisitSchedule />}>
              <Route exact path="/schedule/:scheduleId" element={<ScheduleCalendar />} />
            </Route>
            <Route path="/booking" element={<Booking />} />
            <Route path="/doaLogs" element={<DOA />} />
            <Route path="/doaLogs/myTasks" element={<DOA />} />
            <Route path="/doaLogs/completedTasks" element={<DOA />} />
            <Route path="/doaLogs/:userId" element={<DOA />} />
            <Route path="/doaLogs/configureTasks" element={<DOA />} />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['site_monitor', 'data_manager', 'PI', 'site_coordinator']} />}>
            <Route
              path="/eConcent/:trialId/trial-site/:trialSiteId/site/:siteId/subject/:subjectMasterId/details"
              element={<SubjectDetails />}
            />
            <Route
              exact
              path="/eConcent/:trialId/trial-site/:trialSiteId/site/:siteId/subject/:subjectMasterId/crf/:crfMasterId/:visitStepKey"
              element={<VisitDetailCRFInfo />}
            />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['PI', 'site_coordinator', 'subject']} />}>
            <Route
              path="/eConcent/:trialId/trial-site/:trialSiteId/:siteInitStep/:subjectMasterId?"
              element={<EnrollSubject />}
            />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['PI', 'site_coordinator']} />}>
            <Route
              path="/eConcent/:trialId/trial-site/:trialSiteId/:siteInitStep/:subjectMasterId/withdraw"
              element={<SubjectWithdrawForm />}
            />
            <Route
              path="/eConcent/:trialId/trial-site/:trialSiteId/:siteInitStep/:subjectMasterId/crf/:crfMasterId"
              element={<SubjectCRFForm />}
            />
          </Route>
          {/* FAQ Admin Routes - System Admin & Sponsor */}
          <Route element={<ProtectedRoute user={user} roles={['system_admin', 'sponsor']} />}>
            <Route path="/admin/faqs" element={<FaqList />} />
            <Route path="/admin/faqs/new" element={<FaqForm />} />
            <Route path="/admin/faqs/:id/edit" element={<FaqForm />} />
            <Route path="/admin/faqs/:id/preview" element={<FaqPreview />} />
            <Route path="/admin/faq-categories" element={<CategoryManagement />} />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['system_admin']} />}>
            <Route path="/admin/support-tickets" element={<SupportTicketList />} />
            <Route path="/admin/support-tickets/:id" element={<SupportTicketDetail />} />
            <Route path="/finance" element={<FinanceModule />} />
            <Route path="/finance/:sponsorId" element={<SponsorDetails />} />
            <Route path="/finance/:sponsorId/setup/:studyId?" element={<StudySetup />} />
            {/* <Route path="/sponsor" element={<Sponsor />} /> */}
            <Route path="/configuration" element={<ManageConfiguration />} />
            <Route path="/sponsorDisplay/:sponsorId?" element={<SponsorShow />} />
            <Route path="/sponsorDisplay/:sponsorId/site/:siteId" element={<SiteDetails />} />
            {/* <Route path="/sponsor/createTrial/:sponsorId?" element={<CreateTrials />} /> */}
            <Route path="/pfasTrak" element={<PfasTrack />} />
            <Route path="/pfasTrak/:sponsorId?" element={<PfasOrgDetails />} />
            <Route path="/pfasTrak/:sponsorId/create/assignment/:assignmentId?" element={<AssignmentSetup />} />
            <Route path="/pfasTrak/:sponsorId?/assignment/:assignmentId?" element={<AssignmentDetails />} />
            <Route path="/pfasTrak/:sponsorId/assignment/:assignmentId/vendor/:vendorId?" element={<VendorDetails />} />
            <Route
              exact
              path="/pfasTrak/:sponsorId/assignment/:assignmentId/vendor/:vendorId/product/:productStepKey/:productId?"
              element={<ProductCrf />}
            />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['sponsor', 'fi_admin']} />}>
            <Route path="/trialDetails" element={<TrialDetails />} />
            <Route path="/createTrial/:trialId?" element={<CreateTrial />} />
            <Route path="/createFinanceTrial/:trialId?" element={<CreateFinanceTrial />} />
            <Route exact path="/eTMFs-eISF" element={<ETMFs />} />
            <Route path="/eTMFs-eISF/:trialId/doc/:siteId" element={<ETMFsDocument />} />
            <Route path="/eTMFs-eISF/doc/:trialId" element={<ETMFsDocument />} />
          </Route>

          <Route element={<ProtectedRoute user={user} roles={['subject']} />}>
            <Route
              exact
              path="/eConcent/:trialId/trial-site/:trialSiteId/site/:siteId/subject/:subjectMasterId/crf/:crfMasterId/:visitStepKey/subject"
              element={<SubjectVisitDetailCRFInfo />}
            />
            <Route path="/profile" element={<SubjectProfile />} />
            <Route path="/followup" element={<SlotBooking />} />
            <Route path="/followup/booked-slots/:slotId" element={<BookedVisitSlotDetails />} />
            <Route path="/followup/confirm/:slotId" element={<ConfirmVisit />} />
            <Route
              path="/eConcent/:trialId/trial-site/:trialSiteId/site/:siteId/subject/:subjectMasterId/enrollDetails/:stepKey"
              element={<SubjectEnrollFormDetails />}
            />
            <Route path="/trial/:trialId/site/:siteId/subject/:subjectId/schedule" element={<SubjectSchedule />} />
          </Route>
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </AuthProvider>
  );
};

export default AppRoutes;
